import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Button, Chip, Tab, Tabs } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowRotateLeft,
  faArrowRight,
  faChartBar,
  faClockRotateLeft,
  faDatabase,
  faFileArrowUp,
  faFileInvoice,
  faMagnifyingGlass,
  faScaleBalanced,
  faUserShield,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';

import { TranslationKeys } from '../../../../translations';

import {
  PageWrapperStyled,
  PageHeaderStyled,
  HeaderIconWrapperStyled,
  HeaderTextStyled,
  HeaderTitleStyled,
  HeaderSubtitleStyled,
  TabsContainerStyled,
  FeaturesGridStyled,
  FeatureCardStyled,
  CardTopBandStyled,
  CardIconCircleStyled,
  CardBodyStyled,
  CardTitleStyled,
  CardDescriptionStyled,
  CardFooterStyled,
} from './AdminPanelManagementPage.styles';

type AdminFeature = {
  titleId: string;
  descriptionId: string;
  link: string;
  icon: IconDefinition;
  comingSoon?: boolean;
};

type AdminCategory = {
  titleId: string;
  tabIcon: IconDefinition;
  color: string;
  features: AdminFeature[];
};

// ─── Category + feature data ─────────────────────────────────────────────────

const CATEGORIES: AdminCategory[] = [
  {
    titleId: TranslationKeys.adminPanel_category_inspectionDiagnostics,
    tabIcon: faMagnifyingGlass,
    color: '#00A0FF',
    features: [
      {
        titleId: TranslationKeys.menu_adminPanel_connectionInspector,
        descriptionId: TranslationKeys.adminPanel_feature_connectionInspector_description,
        link: '/admin-panel/connection-inspector',
        icon: faMagnifyingGlass,
      },
      {
        titleId: TranslationKeys.menu_adminPanel_databaseInspector,
        descriptionId: TranslationKeys.adminPanel_feature_databaseInspector_description,
        link: '/admin-panel/database-inspector',
        icon: faDatabase,
        comingSoon: true,
      },
      {
        titleId: TranslationKeys.menu_adminPanel_nmbrsComparator,
        descriptionId: TranslationKeys.adminPanel_feature_nmbrsComparator_description,
        link: '/admin-panel/nmbrs-comparator',
        icon: faScaleBalanced,
        comingSoon: true,
      },
    ],
  },
  {
    titleId: TranslationKeys.adminPanel_category_dataRecovery,
    tabIcon: faArrowRotateLeft,
    color: '#40C484',
    features: [
      {
        titleId: TranslationKeys.menu_adminPanel_restoreConnections,
        descriptionId: TranslationKeys.adminPanel_feature_restoreConnections_description,
        link: '/admin-panel/restore-connections',
        icon: faArrowRotateLeft,
      },
      {
        titleId: TranslationKeys.menu_adminPanel_undoUploadedFile,
        descriptionId: TranslationKeys.adminPanel_feature_undoUploadedFile_description,
        link: '/admin-panel/undo-uploaded-file',
        icon: faClockRotateLeft,
        comingSoon: true,
      },
    ],
  },
  {
    titleId: TranslationKeys.adminPanel_category_operations,
    tabIcon: faFileArrowUp,
    color: '#F3AE43',
    features: [
      {
        titleId: TranslationKeys.menu_adminPanel_naverrekenenTemplateUpload,
        descriptionId: TranslationKeys.adminPanel_feature_naverrekenenTemplateUpload_description,
        link: '/admin-panel/naverrekenen-template',
        icon: faFileArrowUp,
      },
      {
        titleId: TranslationKeys.menu_adminPanel_bulkLeftCompany,
        descriptionId: TranslationKeys.adminPanel_feature_bulkLeftCompany_description,
        link: '/admin-panel/bulk-left-company',
        icon: faUsers,
      },
    ],
  },
  {
    titleId: TranslationKeys.adminPanel_category_analyticsReporting,
    tabIcon: faChartBar,
    color: '#7C4DFF',
    features: [
      {
        titleId: TranslationKeys.menu_adminPanel_stats,
        descriptionId: TranslationKeys.adminPanel_feature_stats_description,
        link: '/admin-panel/stats',
        icon: faChartBar,
        comingSoon: true,
      },
      {
        titleId: TranslationKeys.menu_adminPanel_productInvoicing,
        descriptionId: TranslationKeys.adminPanel_feature_productInvoicing_description,
        link: '/admin-panel/product-invoicing',
        icon: faFileInvoice,
        comingSoon: true,
      },
    ],
  },
];

// ─── Feature card ─────────────────────────────────────────────────────────────

type FeatureCardProps = {
  feature: AdminFeature;
  color: string;
  onNavigate: (link: string) => void;
};

const FeatureCard = ({ feature, color, onNavigate }: FeatureCardProps) => {
  const available = !feature.comingSoon;

  return (
    <FeatureCardStyled
      elevation={0}
      available={available}
      onClick={available ? () => onNavigate(feature.link) : undefined}
    >
      <CardTopBandStyled bandcolor={color}>
        <CardIconCircleStyled bandcolor={color}>
          <FontAwesomeIcon icon={feature.icon} />
        </CardIconCircleStyled>
        {feature.comingSoon && (
          <Chip
            label={<FormattedMessage id={TranslationKeys.adminPanel_comingSoon} />}
            size="small"
            sx={{
              backgroundColor: `${color}22`,
              color,
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 22,
              letterSpacing: '0.03em',
            }}
          />
        )}
      </CardTopBandStyled>

      <CardBodyStyled>
        <CardTitleStyled variant="body1">
          <FormattedMessage id={feature.titleId} />
        </CardTitleStyled>
        <CardDescriptionStyled variant="body2">
          <FormattedMessage id={feature.descriptionId} />
        </CardDescriptionStyled>
      </CardBodyStyled>

      <CardFooterStyled>
        {available ? (
          <Button
            size="small"
            variant="text"
            endIcon={<FontAwesomeIcon icon={faArrowRight} />}
            sx={{ color, fontWeight: 600 }}
          >
            <FormattedMessage id={TranslationKeys.adminPanel_open} />
          </Button>
        ) : (
          <Button size="small" variant="text" disabled sx={{ fontSize: '0.75rem' }}>
            <FormattedMessage id={TranslationKeys.adminPanel_comingSoon} />
          </Button>
        )}
      </CardFooterStyled>
    </FeatureCardStyled>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const AdminPanelManagementPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState(0);

  const handleTabChange = React.useCallback((_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  }, []);

  const handleNavigate = React.useCallback(
    (link: string) => {
      navigate(link);
    },
    [navigate],
  );

  const activeCategory = CATEGORIES[activeTab];

  return (
    <PageWrapperStyled>
      {/* Header */}
      <PageHeaderStyled elevation={0}>
        <HeaderIconWrapperStyled>
          <FontAwesomeIcon icon={faUserShield} />
        </HeaderIconWrapperStyled>
        <HeaderTextStyled>
          <HeaderTitleStyled variant="h4">
            <FormattedMessage id={TranslationKeys.adminPanel_header_title} />
          </HeaderTitleStyled>
          <HeaderSubtitleStyled variant="body2">
            <FormattedMessage id={TranslationKeys.adminPanel_header_description} />
          </HeaderSubtitleStyled>
        </HeaderTextStyled>
      </PageHeaderStyled>

      {/* Category tabs + feature grid */}
      <TabsContainerStyled elevation={0}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
            '& .MuiTab-root': { minHeight: 56, textTransform: 'none', fontWeight: 600, gap: 1 },
          }}
        >
          {CATEGORIES.map((cat, idx) => (
            <Tab
              key={idx}
              icon={<FontAwesomeIcon icon={cat.tabIcon} style={{ color: cat.color }} />}
              iconPosition="start"
              label={<FormattedMessage id={cat.titleId} />}
            />
          ))}
        </Tabs>

        <FeaturesGridStyled>
          {activeCategory.features.map(feature => (
            <FeatureCard
              key={feature.titleId}
              feature={feature}
              color={activeCategory.color}
              onNavigate={handleNavigate}
            />
          ))}
        </FeaturesGridStyled>
      </TabsContainerStyled>
    </PageWrapperStyled>
  );
};
